import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { SDManager } from './SDManager'
import { D3SimulationManager } from './D3SimulationManager'
import ToggleSwitch2 from '../ToggleSwitch2'

export interface NetworkView3Ref {
    sdManager: SDManager | null
}

const NetworkView = forwardRef<NetworkView3Ref>((_, ref) => {
    const [showActiveOnly, setShowActiveOnly] = useState(false)
    const svgRef = useRef<SVGSVGElement | null>(null)
    // Instead of storing in a ref, we use state so that updates trigger a re-render.
    const [sdManager, setSdManager] = useState<SDManager | null>(null)
    const [dimensions, setDimensions] = useState<{
        width: number
        height: number
    } | null>(null)
    const [showStatusInfo] = useState(true)

    // Expose the sdManager to the parent component.
    useImperativeHandle(
        ref,
        () => ({
            sdManager,
            showStatusInfo,
        }),
        [sdManager, showStatusInfo],
    )

    useEffect(() => {
        if (!svgRef.current) return

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0]
            const { width, height } = entry.contentRect
            if (width && height) {
                setDimensions({ width, height })
            }
        })

        observer.observe(svgRef.current)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!svgRef.current) return
        if (!dimensions) return
        // only create the sdManager once
        if (sdManager) return

        const simManager = new D3SimulationManager(
            svgRef.current,
            dimensions.width,
            dimensions.height,
        )
        const newSdManager = new SDManager(simManager)
        newSdManager.setShowStatusInfo(showStatusInfo)
        setSdManager(newSdManager)
    }, [dimensions, sdManager, showStatusInfo])

    // Toolbar handlers
    function handleShowActiveOnlyChange(checked: boolean) {
        if (sdManager) {
            sdManager.setShowEnabledOnly(checked)
        }
        setShowActiveOnly(checked)
    }

    return (
        <div>
            <div className="mb-2">
                <ToggleSwitch2
                    label="Show Active Only:"
                    checked={showActiveOnly}
                    onChange={handleShowActiveOnlyChange}
                />
            </div>
            <svg
                className="border bg-white rounded-lg w-full h-[800px]"
                ref={svgRef}
                viewBox={
                    dimensions
                        ? `0 0 ${dimensions.width} ${dimensions.height}`
                        : undefined
                }
                preserveAspectRatio="xMidYMid meet"
            />
        </div>
    )
})

export default NetworkView
